import React, { useState, useEffect } from 'react'
import { PlayIcon, DocumentTextIcon, BeakerIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { getLocalApiUrl, authFetch } from '../../config/api'

import { formatSydneyDateTime, formatSydneyTime } from '../../utils/sydneyDateTime'
const SAMPLE_TEST_DATA = {
  transaction: {
    CashIn: 15000,
    CashOut: 2000,
    CashDrop: 500,
    Turnover: 18000,
    TransactionType: 'CASH_IN',
    MemberID: 'M001234',
    EgmHouseNo: 'EGM001',
    VenueCode: 'VEN001',
    SnapShotTime: new Date().toISOString()
  },
  session: {
    SessionPK: 'SES_001234',
    MemberID: 'M001234',
    SessionDuration: 25200, // 7 hours in seconds
    TotalCashIn: 45000,
    TotalCashOut: 38000,
    NetPosition: -7000,
    StartTime: new Date(Date.now() - 7 * 60 * 60 * 1000).toISOString(),
    EndTime: new Date().toISOString()
  },
  onboarding: {
    MemberID: 'M001234',
    FirstName: 'John',
    LastName: 'Doe',
    Country: 'US',
    DateOfBirth: '1985-05-15',
    RegistrationDate: new Date().toISOString(),
    InitialDeposit: 5000,
    VerificationStatus: 'PENDING'
  },
  compliance: {
    MemberID: 'M001234',
    FirstName: 'John',
    LastName: 'Doe',
    Country: 'US',
    dowjones: {
      pep_match: true,
      pep_score: 85,
      sanctions_match: false,
      watchlist_match: false,
      risk_score: 75,
      last_checked: new Date().toISOString()
    }
  }
}

// Test fixture config — sample JSON payloads for rule testing only. Rules list: DB-first; optional explicit engine load.

function Testing() {
  const [selectedRule, setSelectedRule] = useState('')
  const [rules, setRules] = useState([])
  const [isLoadingRules, setIsLoadingRules] = useState(false)
  const [loadError, setLoadError] = useState('')
  /** 'db' = /api/rules/db, 'engine' = user opted into /api/rules (in-memory snapshot) */
  const [rulesSource, setRulesSource] = useState(null)
  const [testDataType, setTestDataType] = useState('transaction')
  const [customTestData, setCustomTestData] = useState('')
  const [useCustomData, setUseCustomData] = useState(false)
  const [testResults, setTestResults] = useState(null)
  const [isRunningTest, setIsRunningTest] = useState(false)
  const [testHistory, setTestHistory] = useState([])
  const [memberId, setMemberId] = useState('M001234')

  const normalizeDbRule = (r) => ({
    id: r.rule_id || r.id,
    name: r.name,
    type: r.type,
    category: r.category,
    severity: (r.severity || '').toString().toUpperCase(),
    processing_category: r.processing_category,
    aggregation: r.aggregation,
    conditions: r.conditions,
  })

  const normalizeEngineRule = (r) => ({
    id: r.id,
    name: r.name,
    type: r.type,
    category: r.category,
    severity: (r.severity || '').toString().toUpperCase(),
    processing_category: r.processing_category,
    aggregation: r.aggregation,
    conditions: r.conditions,
  })

  const loadDbRules = async () => {
    setIsLoadingRules(true)
    setLoadError('')
    setRulesSource(null)
    try {
      const resp = await authFetch(getLocalApiUrl('/api/rules/db'))
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      const json = await resp.json()
      if (json?.success && Array.isArray(json.data) && json.data.length >= 0) {
        const normalized = json.data.map(normalizeDbRule)
        setRules(normalized)
        setRulesSource('db')
        if (normalized.length) setSelectedRule((prev) => prev || normalized[0].id)
        return
      }
      throw new Error(json?.error || 'No rules returned from database')
    } catch (e) {
      setRules([])
      setRulesSource(null)
      setLoadError(e?.message || 'Failed to load rules from /api/rules/db')
    } finally {
      setIsLoadingRules(false)
    }
  }

  const loadEngineRules = async () => {
    setIsLoadingRules(true)
    setLoadError('')
    try {
      const resp = await authFetch(getLocalApiUrl('/api/rules'))
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      const json = await resp.json()
      if (json?.success && Array.isArray(json.data)) {
        const normalized = json.data.map(normalizeEngineRule)
        setRules(normalized)
        setRulesSource('engine')
        if (normalized.length) setSelectedRule((prev) => prev || normalized[0].id)
        return
      }
      throw new Error(json?.error || 'Invalid response from /api/rules')
    } catch (e) {
      setLoadError(e?.message || 'Failed to load in-memory engine rules')
      setRules([])
      setRulesSource(null)
    } finally {
      setIsLoadingRules(false)
    }
  }

  useEffect(() => {
    loadDbRules()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const runTest = async () => {
    if (!selectedRule) {
      toast.error('Please select a rule to test')
      return
    }

    setIsRunningTest(true)
    setTestResults(null)

    try {
      const rule = rules.find(r => r.id === selectedRule)
      let testData

      if (useCustomData && customTestData) {
        try {
          testData = JSON.parse(customTestData)
        } catch (error) {
          toast.error('Invalid JSON in custom test data')
          setIsRunningTest(false)
          return
        }
      } else {
        testData = SAMPLE_TEST_DATA[testDataType]
      }

      // Real API call to test rule
      const testUrl = getLocalApiUrl(`/api/rules/${rule.id}/test`)
      console.log('📡 Testing page making API call to:', testUrl, 'with data:', testData)
      const response = await authFetch(testUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          testType: 'single',
          data: testData
        })
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const result = await response.json()

      if (result.success) {
        // Find the result for the selected rule (engine returns only triggered results)
        const selected = (result.data?.results || []).find((r) => r.ruleId === rule.id)

        const realResults = {
          ruleId: rule.id,
          ruleName: rule.name,
          ruleType: rule.type,
          category: rule.category,
          severity: rule.severity,
          triggered: !!selected,
          confidence: selected?.confidence || 0,
          timestamp: selected?.timestamp || new Date().toISOString(),
          testData,
          details: {
            evaluatedConditions: selected?.conditionResults || [],
            processingTime: result.data?.summary?.processingTime || 0
          }
        }

        setTestResults(realResults)
        
        // Add to test history
        const historyEntry = {
          id: Date.now(),
          timestamp: new Date().toISOString(),
          rule: rule.name,
          ruleId: rule.id,
          dataType: testDataType,
          triggered: realResults.triggered,
          confidence: realResults.confidence
        }
        setTestHistory(prev => [historyEntry, ...prev.slice(0, 9)]) // Keep last 10 tests

        toast.success('Test completed successfully')
      } else {
        setTestResults({
          ruleId: rule.id,
          ruleName: rule.name,
          ruleType: rule.type,
          category: rule.category,
          severity: rule.severity,
          triggered: false,
          confidence: 0,
          timestamp: new Date().toISOString(),
          testData,
          details: { evaluatedConditions: [], processingTime: 0 }
        })
        toast('No rules triggered', { icon: 'ℹ️' })
      }
    } catch (error) {
      console.error('Error testing rule:', error)
      toast.error('Failed to test rule: ' + error.message)
    } finally {
      setIsRunningTest(false)
    }
  }

  const buildSampleData = (type) => {
    const base = { ...SAMPLE_TEST_DATA[type] }
    base.MemberID = memberId
    if (type === 'transaction') {
      const sel = rules.find(r => r.id === selectedRule)
      if (sel?.processing_category === 'cumulative' && Array.isArray(sel?.aggregation) && sel.aggregation.includes('24h')) {
        base.CashIn = 11000
        base.Turnover = 11000
      } else if ((selectedRule || '').includes('ZERO_TURNOVER')) {
        base.CashIn = 0
        base.CashOut = 0
        base.Turnover = 0
        base.CashDrop = 500
      }
    } else if (type === 'session') {
      base.StartTime = new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString()
      base.EndTime = new Date().toISOString()
      base.SessionDuration = 6 * 60 * 60
    }
    return base
  }

  const getCurrentTestData = () => {
    if (useCustomData && customTestData) {
      try { return JSON.parse(customTestData) } catch { return {} }
    }
    return buildSampleData(testDataType)
  }

  const loadSampleData = (type) => {
    setTestDataType(type)
    // tailor a triggering sample when possible
    let data = { ...SAMPLE_TEST_DATA[type] }
    data.MemberID = memberId
    if (type === 'transaction') {
      // If selected rule is cumulative >=10k in 24h, provide a second record sample
      const sel = rules.find(r => r.id === selectedRule)
      if (sel?.processing_category === 'cumulative' && Array.isArray(sel?.aggregation) && sel.aggregation.includes('24h')) {
        data.CashIn = 11000
        data.Turnover = 11000
      } else if ((selectedRule || '').includes('ZERO_TURNOVER')) {
        data.CashIn = 0
        data.CashOut = 0
        data.Turnover = 0
        data.CashDrop = 500
      }
    } else if (type === 'session') {
      // 6-hour session to trigger duration rules
      data.StartTime = new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString()
      data.EndTime = new Date().toISOString()
      data.SessionDuration = 6 * 60 * 60
    }
    setCustomTestData(JSON.stringify(data, null, 2))
  }

  // Prime history helper: send N transactions to engine for selected rule/member
  const primeHistory = async (count = 2, amount = 6000) => {
    try {
      const base = {
        MemberID: memberId,
        CashIn: amount,
        CashOut: 0,
        CashDrop: 0,
        Turnover: amount,
        TransactionType: 'CASH_IN',
        EgmHouseNo: 'EGM001',
        VenueCode: 'VEN001'
      }
      // Stagger timestamps slightly within last hour
      for (let i = 0; i < count; i++) {
        const ts = new Date(Date.now() - (count - i) * 60 * 1000).toISOString()
        const rec = { ...base, SnapShotTime: ts }
        await authFetch(getLocalApiUrl(`/api/rules/${selectedRule || 'TXN_24H_CASHIN_10K'}/test`), {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ testType: 'single', data: rec })
        })
      }
      toast.success('History primed')
    } catch (e) {
      toast.error('Failed to prime history')
    }
  }

  // Auto-refresh sample preview when switching rules or types (if using sample data)
  useEffect(() => {
    if (!useCustomData) {
      loadSampleData(testDataType)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRule, testDataType, memberId])

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Rule Testing</h1>
        <p className="text-sm text-gray-500">
          Test your rules with sample or custom data to validate their behavior
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Test Configuration */}
        <div className="lg:col-span-2 space-y-6">
          {/* Rule Selection */}
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Select Rule to Test</h3>
            {isLoadingRules && <p className="text-sm text-gray-500 mb-3">Loading rules…</p>}
            {loadError && rules.length === 0 && (
              <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                <p className="font-medium">Could not load published rules from the database</p>
                <p className="mt-1 text-amber-800">{loadError}</p>
                <button
                  type="button"
                  className="mt-2 rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100"
                  onClick={loadEngineRules}
                  disabled={isLoadingRules}
                >
                  Load in-memory engine rules (legacy)
                </button>
                <button
                  type="button"
                  className="mt-2 ml-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-800 hover:bg-gray-50"
                  onClick={loadDbRules}
                  disabled={isLoadingRules}
                >
                  Retry database
                </button>
              </div>
            )}
            {rulesSource === 'engine' && rules.length > 0 && (
              <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                <strong>Rules source:</strong> in-process engine snapshot (<code className="text-[11px]">GET /api/rules</code>), not the published
                DB catalog. Use <strong>Retry database</strong> for tenant-published definitions when available.
                <button
                  type="button"
                  className="ml-2 font-semibold underline"
                  onClick={loadDbRules}
                  disabled={isLoadingRules}
                >
                  Retry database
                </button>
              </div>
            )}
            {rulesSource === 'db' && rules.length > 0 && (
              <p className="mb-3 text-xs text-gray-500">Rules from published catalog (<code className="text-[11px]">/api/rules/db</code>).</p>
            )}
            {!isLoadingRules && rules.length === 0 && !loadError && (
              <p className="mb-3 text-sm text-gray-600">No rules in the database for this tenant. Publish rules or use engine rules for local debugging.</p>
            )}
            <div className="space-y-3">
                {rules.map((rule) => (
                <label key={rule.id} className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name="selectedRule"
                    value={rule.id}
                    checked={selectedRule === rule.id}
                    onChange={(e) => setSelectedRule(e.target.value)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <div className="ml-3 flex-1">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-gray-900">{rule.name}</div>
                     <div className="flex items-center space-x-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 capitalize">
                          {rule.type}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          rule.severity === 'CRITICAL' ? 'bg-red-100 text-red-800' :
                          rule.severity === 'HIGH' ? 'bg-orange-100 text-orange-800' :
                          rule.severity === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {rule.severity}
                        </span>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">{rule.id}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

           {/* Test Data Configuration */}
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Test Data</h3>
            
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Member #</label>
                  <input className="input-field" value={memberId} onChange={(e)=>setMemberId(e.target.value)} />
                </div>
                <div className="mt-6">
                  <button className="btn-secondary text-sm" onClick={()=>primeHistory(2, 6000)}>Prime 24h window (2 x 6000)</button>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="dataSource"
                    checked={!useCustomData}
                    onChange={() => setUseCustomData(false)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-900">Use Sample Data</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="dataSource"
                    checked={useCustomData}
                    onChange={() => setUseCustomData(true)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-900">Use Custom Data</span>
                </label>
              </div>

              {!useCustomData && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sample Data Type
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {Object.keys(SAMPLE_TEST_DATA).map((type) => (
                      <button
                        key={type}
                        onClick={() => setTestDataType(type)}
                        className={`p-2 text-sm rounded-lg border ${
                          testDataType === type
                            ? 'border-primary-500 bg-primary-50 text-primary-700'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {useCustomData ? 'Custom Test Data (JSON)' : 'Sample Data Preview'}
                  </label>
                  {!useCustomData && (
                    <div className="flex space-x-2">
                      {Object.keys(SAMPLE_TEST_DATA).map((type) => (
                        <button
                          key={type}
                          onClick={() => (useCustomData ? loadSampleData(type) : setTestDataType(type))}
                          className="text-xs text-primary-600 hover:text-primary-500"
                        >
                          Load {type}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <textarea
                  value={useCustomData ? customTestData : JSON.stringify(getCurrentTestData(), null, 2)}
                  onChange={(e) => useCustomData && setCustomTestData(e.target.value)}
                  readOnly={!useCustomData}
                  rows={12}
                  className={`input-field font-mono text-sm ${!useCustomData ? 'bg-gray-50' : ''}`}
                  placeholder={useCustomData ? 'Enter your custom test data as JSON...' : ''}
                />
              </div>
            </div>
          </div>

          {/* Run Test Button */}
          <div className="flex justify-center">
            <button
              onClick={runTest}
              disabled={isRunningTest || !selectedRule}
              className="btn-primary flex items-center px-8 py-3 text-lg"
            >
              {isRunningTest ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                  Running Test...
                </>
              ) : (
                <>
                  <PlayIcon className="h-5 w-5 mr-3" />
                  Run Test
                </>
              )}
            </button>
          </div>
        </div>

        {/* Results and History */}
        <div className="space-y-6">
          {/* Test Results */}
          {testResults && (
            <div className="card">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Test Results</h3>
              
              <div className={`p-4 rounded-lg mb-4 ${
                testResults.triggered 
                  ? 'bg-red-50 border border-red-200' 
                  : 'bg-green-50 border border-green-200'
              }`}>
                <div className="flex items-center mb-2">
                  <div className={`w-3 h-3 rounded-full mr-3 ${
                    testResults.triggered ? 'bg-red-500' : 'bg-green-500'
                  }`}></div>
                  <h4 className={`font-medium ${
                    testResults.triggered ? 'text-red-900' : 'text-green-900'
                  }`}>
                    Rule {testResults.triggered ? 'Triggered' : 'Not Triggered'}
                  </h4>
                </div>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Confidence:</span>
                    <span className="font-medium">{testResults.confidence}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Processing Time:</span>
                    <span className="font-medium">{testResults.details.processingTime}ms</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Timestamp:</span>
                    <span className="font-medium">
                      {formatSydneyTime(new Date(testResults.timestamp))}
                    </span>
                  </div>
                </div>
              </div>

              {testResults.details?.evaluatedConditions && (
                <div>
                  <h5 className="text-sm font-medium text-gray-700 mb-2">Condition Results:</h5>
                  <div className="space-y-2">
                    {testResults.details.evaluatedConditions.map((cond, index) => (
                      <div key={index} className="text-sm bg-gray-50 p-3 rounded">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-gray-900">
                            {cond.condition.field} {cond.condition.operator} {cond.condition.value}
                          </span>
                          <span className={`px-2 py-1 rounded text-xs ${
                            cond.result ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {cond.result ? 'Pass' : 'Fail'}
                          </span>
                        </div>
                        <div className="text-gray-600">
                          Actual: {cond.actualValue} | Expected: {cond.condition.operator} {cond.expectedValue}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Test History */}
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Test History</h3>
            
            {testHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <BeakerIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No tests run yet</p>
                <p className="text-sm">Run your first test to see results here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {testHistory.map((test) => (
                  <div key={test.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">{test.rule}</div>
                      <div className="text-xs text-gray-500">
                        {formatSydneyDateTime(new Date(test.timestamp))} • {test.dataType}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500">{test.confidence}%</span>
                      <div className={`w-2 h-2 rounded-full ${
                        test.triggered ? 'bg-red-400' : 'bg-green-400'
                      }`}></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Tips */}
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Testing Tips</h3>
            <div className="space-y-3 text-sm text-gray-600">
              <div className="flex items-start">
                <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <p>Test rules with edge cases and boundary values to ensure robust detection</p>
              </div>
              <div className="flex items-start">
                <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <p>Use custom data to simulate real-world scenarios from your environment</p>
              </div>
              <div className="flex items-start">
                <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <p>Review condition results to understand why rules trigger or don't trigger</p>
              </div>
              <div className="flex items-start">
                <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <p>Test cumulative rules with historical data to validate aggregation logic</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Testing
